<template>
  <div>
    <div :class="containerClass" class="d-flex justify-content-between mt-3">
      <h2>
        View Courses
        <i class="material-icons icon-16pt clickable-item" v-b-popover.hover.top="`View courses of each program.`"
          >info</i
        >
      </h2>
      <div>
        <b-btn size="md" variant="primary" :to="{ name: 'create-course' }">
          Add Course
        </b-btn>
      </div>
    </div>

    <div class="page-section">
      <div :class="containerClass">
        <div class="filters-panel mb-4">
          <div class="filters-toolbar">
            <form class="filters-toolbar__search search-form search-form--light" @submit.prevent="">
              <b-form-input placeholder="Search Courses" v-model="searchTerm" @input="onSearch" />
              <b-btn variant="flush" type="submit">
                <md-icon>search</md-icon>
              </b-btn>
            </form>

            <div class="filters-toolbar__select">
              <v-select
                id="prog_category"
                class="form-control v-select-custom"
                label="title"
                v-model="program_category"
                :reduce="pg => pg.id"
                placeholder="All Program Categories"
                :options="allPrograms"
                :loading="areProgramsLoading"
                @input="onProgramChange"
              >
              </v-select>
            </div>

            <div class="filters-toolbar__tabs">
              <div class="courses-tabs">
                <button
                  type="button"
                  class="courses-tabs__btn"
                  :class="{ 'courses-tabs__btn--active': activeTab === 'transition' }"
                  @click="setActiveTab('transition')"
                >
                  Transition Courses
                </button>
                <button
                  type="button"
                  class="courses-tabs__btn"
                  :class="{ 'courses-tabs__btn--active': activeTab === 'my' }"
                  @click="setActiveTab('my')"
                >
                  My Courses
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="courses-grid-section">
          <div v-if="isLoading" class="text-center my-5">
            <b-spinner class="align-middle mr-2"></b-spinner>
            <strong>Loading courses...</strong>
          </div>

          <div v-else-if="courses.length" class="row courses-grid">
            <div v-for="course in courses" :key="course.id" class="col-12 col-sm-6 col-lg-6 col-xl-4 mb-4">
              <div
                class="course-card h-100"
                :class="{ 'course-card--transition': course.is_superuser_course }"
                @click="handleCourseClick(course)"
              >
                <div class="course-card__image">
                  <img v-if="course.image_url" :src="course.image_url" alt="Course cover" />
                  <div v-else class="course-card__placeholder">
                    <i class="material-icons">school</i>
                  </div>
                </div>

                <div class="course-card__body">
                  <div class="course-card__title-row">
                    <h5 class="course-card__title">{{ course.title }}</h5>
                    <div class="course-card__menu" @click.stop>
                      <b-dropdown :boundary="'main'" no-flip variant="flush" toggle-class="text-muted" no-caret>
                        <template #button-content>
                          <i class="material-icons icon-24pt text-info">more_vert</i>
                        </template>
                        <b-dropdown-item
                          v-if="!course.is_superuser_course"
                          :to="{
                            name: 'edit-lms-course',
                            params: { id: course.id },
                          }"
                        >
                          <md-icon class="text-info mr-2">edit</md-icon>
                          {{ $t('generalMsgs.edit') }}
                        </b-dropdown-item>
                        <b-dropdown-item
                          v-if="course.is_superuser_course"
                          :to="{
                            name: 'view-lms-course',
                            params: { id: course.id },
                          }"
                        >
                          <md-icon class="text-info mr-2">menu_book</md-icon>
                          View Course
                        </b-dropdown-item>
                        <b-dropdown-item
                          :to="{
                            name: 'course-enrollments',
                            params: { id: course.id },
                          }"
                        >
                          <md-icon class="text-info mr-2">visibility</md-icon>
                          View Enrollments
                        </b-dropdown-item>
                        <!-- <b-dropdown-item
                          :to="{
                            name: 'course-analytics',
                            params: { id: course.id },
                          }"
                        >
                          <md-icon class="text-info mr-2">bar_chart</md-icon>
                          Course Report
                        </b-dropdown-item> -->
                        <b-dropdown-item
                          :to="{
                            name: 'course-gradebook',
                            params: { id: course.id },
                          }"
                        >
                          <md-icon class="text-info mr-2">assignment</md-icon>
                          Gradebook
                        </b-dropdown-item>

                        <!-- <b-dropdown-item
                          :to="{
                            name: 'course-analytics',
                            params: { id: course.id },
                          }"
                        >
                          <md-icon class="text-info mr-2">analytics</md-icon>
                          Course Report
                        </b-dropdown-item> -->
                        <b-dropdown-divider v-if="!course.is_superuser_course"></b-dropdown-divider>
                        <b-dropdown-item v-if="!course.is_superuser_course" @click="confirmDeleteCourse(course.id)">
                          <md-icon class="text-danger mr-2">delete</md-icon>
                          {{ $t('generalMsgs.remove') }}
                        </b-dropdown-item>
                      </b-dropdown>
                    </div>
                  </div>
                  <div v-if="course.is_superuser_course" class="course-card__tag">Transition Course</div>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="text-center py-4">
            <p class="text-muted">{{ emptyStateMessage }}</p>
          </div>
        </div>

        <div class="courses-pagination" v-if="courses.length">
          <pagination
            v-model="currentPage"
            :total-rows="totalCourses"
            :per-page="perPage"
            @change="onPageChange"
            aria-controls="courses-grid"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce, get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Page from '@/components/Page.vue';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants';
import Pagination from '../../../components/Ui/Pagination.vue';
import persistListFilters from '@/mixins/persist-list-filters';

export default {
  components: {
    Pagination,
  },
  extends: Page,
  mixins: [persistListFilters],
  filterKeys: ['searchTerm', 'program_category', 'currentPage', 'offset'],

  data() {
    return {
      title: 'View Courses',
      date: null,
      isLoading: true,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalCourses: 0,
      searchTerm: null,
      program: null,
      allPrograms: [],
      program_category: '',
      areProgramsLoading: false,
      windowWidth: window.innerWidth,
      courses: [],
      limit: 15,
      offset: 0,
      search: '',
      activeTab: 'my',
      isTabSwitching: false,
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    isTabSmallScreen() {
      return this.windowWidth <= 767;
    },
    emptyStateMessage() {
      return this.activeTab === 'transition' ? 'No transition courses found' : 'No courses found';
    },
  },

  methods: {
    get,
    ...mapActions('courses', ['getLmsCourses', 'deleteCourse']),
    ...mapActions('program', ['getAllPrograms']),

    clearFilters() {
      this.program = null;
      this.tag_id = null;
    },
    dateChange(value) {
      if (value[0]) {
        this.date = value;
      } else {
        this.date = null;
      }
    },
    onPageChange(pageNum) {
      this.offset = (pageNum - 1) * this.perPage;
      this.fetchAllCourses();
    },
    async fetchPrograms() {
      this.areProgramsLoading = true;
      try {
        const res = await this.getAllPrograms();
        this.allPrograms = res.data;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areProgramsLoading = false;
    },
    async confirmDeleteCourse(id) {
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm('Are you sure you want to delete the Course?', {
          title: 'Delete Course',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'success',
          cancelVariant: 'danger',
          okTitle: 'Yes',
          cancelTitle: 'No',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        });
        if (isConfirmed) {
          await this.onDeleteCourse(id);
          this.fetchAllCourses();
          // const refreshPage = this.careers.length > 1 ? this.currentPage : this.currentPage - 1;
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    async onDeleteCourse(id) {
      try {
        await this.deleteCourse(id);
        this.makeToast({ variant: 'success', msg: 'Course Successfully Deleted' });
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    async fetchAllCourses(params = {}) {
      this.isLoading = true;
      try {
        const res = await this.getLmsCourses({
          limit: this.perPage,
          offset: this.offset,
          ...(this.searchTerm && { search: this.searchTerm }),
          ...(this.program_category ? { program_category__id: this.program_category } : {}),
          pagination: true,
          ...this.tabFilterParams(),
          ...params,
        });

        this.courses = res.data.results;
        this.totalCourses = res.data.count;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    onSearch() {
      // Reset to the first page when searching so the paginator and the
      // requested offset stay in sync.
      this.currentPage = 1;
      this.offset = 0;
      this.debouncedCourseSearch(this);
    },

    onProgramChange() {
      // Reset to the first page when the program-category filter changes.
      this.currentPage = 1;
      this.offset = 0;
      this.fetchAllCourses();
    },

    debouncedCourseSearch: debounce(vm => {
      vm.fetchAllCourses();
    }, 500),

    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    handleCourseClick(course) {
      if (course.is_superuser_course) {
        this.$router.push({
          name: 'view-lms-course',
          params: { id: course.id },
        });
      } else {
        this.$router.push({
          name: 'edit-lms-course',
          params: { id: course.id },
        });
      }
    },
    async setActiveTab(tab) {
      if (this.activeTab === tab || this.isTabSwitching) {
        return;
      }
      this.isTabSwitching = true;
      this.activeTab = tab;
      this.currentPage = 1;
      this.offset = 0;
      await this.fetchAllCourses();
      this.isTabSwitching = false;
    },
    tabFilterParams() {
      if (this.activeTab === 'transition') {
        return { is_superuser_course: true };
      }
      return { is_superuser_course: false };
    },
    courseProgramTitle(course) {
      return course.program_category && course.program_category.title
        ? course.program_category.title
        : 'No program assigned';
    },
    courseDeliveryMode(course) {
      return (
        course.delivery_mode ||
        course.delivery_type ||
        course.learning_mode ||
        (course.is_superuser_course ? 'Transition Course' : 'My Course')
      );
    },
    truncateText(text, length = 110) {
      if (!text) {
        return 'No description added yet.';
      }
      return text.length > length ? `${text.slice(0, length).trim()}…` : text;
    },
  },

  async mounted() {
    await this.fetchPrograms();
    await this.fetchAllCourses();
    // If My Courses tab is empty on initial load, fall back to Transition Courses tab
    if (this.activeTab === 'my' && !this.totalCourses) {
      this.activeTab = 'transition';
      await this.fetchAllCourses();
    }
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style scoped lang="scss">
.loader {
  text-align: center;
  color: #bbbbbb;
  margin-inline: auto;
}

.filters-panel {
  background: transparent;
  margin-bottom: 1.75rem;
}

.filters-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: space-between;
}

.filters-toolbar__search {
  flex: 1 1 280px;
  max-width: 420px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border: 1px solid rgba(23, 44, 99, 0.14);
  border-radius: 10px;
  background: #ffffff;
  height: 44px;
}

.filters-toolbar__search .form-control {
  border: none;
  padding: 0;
  box-shadow: none;
  height: 100%;
  flex: 1 1 auto;
}

.filters-toolbar__search .btn {
  border-radius: 8px;
  height: 30px;
  width: 30px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #4a5980;
}

.filters-toolbar__select {
  flex: 0 1 170px;
  max-width: 190px;
  height: 44px;
  display: flex;
}

.filters-toolbar__select ::v-deep .v-select,
.filters-toolbar__select ::v-deep .vs__dropdown-toggle {
  height: 100%;
}

.filters-toolbar__select ::v-deep .vs__dropdown-toggle {
  border-radius: 10px;
  padding: 0 12px;
  border-color: rgba(23, 44, 99, 0.14);
  min-height: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  box-sizing: border-box;
}

.filters-toolbar__select ::v-deep .vs__selected,
.filters-toolbar__select ::v-deep .vs__search {
  line-height: 1.2;
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0;
}

.filters-toolbar__select ::v-deep .vs__actions {
  align-items: center;
  height: 100%;
}

.filters-toolbar__select ::v-deep .vs__selected-options {
  display: flex;
  align-items: center;
  height: 100%;
}

.filters-toolbar__tabs {
  flex: 0 0 auto;
  height: 44px;
}

.courses-tabs {
  display: inline-flex;
  border-radius: 10px;
  border: 1px solid rgba(23, 44, 99, 0.14);
  overflow: hidden;
  height: 100%;
}

.courses-tabs__btn {
  border: none;
  background: transparent;
  padding: 0 18px;
  font-weight: 600;
  font-size: 0.95rem;
  color: #4a5980;
  border-right: 1px solid rgba(23, 44, 99, 0.08);
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  height: 100%;
}

.courses-tabs__btn:last-child {
  border-right: none;
}

.courses-tabs__btn:hover {
  background: rgba(var(--primary-color-rgb, 42, 77, 208), 0.1);
}

.courses-tabs__btn--active {
  background: var(--primary-color);
  color: #fff;
  box-shadow: none;
}

@media (max-width: 991.98px) {
  .filters-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .filters-toolbar__search,
  .filters-toolbar__select,
  .filters-toolbar__tabs {
    width: 100%;
    max-width: none;
  }

  .courses-tabs {
    justify-content: space-between;
  }
}

.courses-grid {
  margin: 0 -0.5rem;
}

.course-card {
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 8px 22px rgba(33, 33, 52, 0.14);
  border: 1px solid rgba(18, 38, 63, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  overflow: visible;
  position: relative;
  z-index: 1;

  &--transition {
    box-shadow: 0 12px 28px rgba(39, 66, 135, 0.2);
    border-color: rgba(39, 66, 135, 0.3);

    .course-card__title {
      color: #1e2e66;
    }
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 32px rgba(18, 38, 63, 0.18);
    z-index: 3;
  }
}

.course-card__image {
  position: relative;
  width: 100%;
  padding-top: 62%;
  overflow: hidden;
  border-top-left-radius: 18px;
  border-top-right-radius: 18px;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.course-card__placeholder {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #f2f6ff, #e6ecff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);

  i {
    font-size: 48px;
  }
}

.course-card__body {
  padding: 18px 22px 22px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  position: relative;
}

.course-card__title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.course-card__menu {
  position: relative;
  top: -4px;
  right: -4px;
}

.course-card__menu ::v-deep .dropdown-menu {
  z-index: 2000;
  border-radius: 12px;
  box-shadow: 0 12px 24px rgba(18, 38, 63, 0.18);
  overflow: hidden;
}

.course-card__menu ::v-deep .course-report-link {
  padding-top: 0;
  padding-bottom: 0;
}

.course-report-pill {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 999px;
  background: var(--primary-color);
  color: #fff;
  padding: 10px 16px;
  font-weight: 600;
  min-width: 170px;
  box-shadow: 0 10px 20px rgba(var(--primary-color-rgb, 42, 77, 208), 0.25);
}

.course-report-pill md-icon {
  color: #fff;
}

.course-card__title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0;
  color: #1e2334;
  text-transform: capitalize;
}

.course-card__tag {
  margin-top: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--primary-color);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.course-card__footer {
  position: absolute;
  left: 16px;
  bottom: 16px;
}
</style>
